import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPatch, apiPost } from '../../lib/api';

/**
 * Catalogul de pachete, dinspre client.
 *
 * Stă lângă `settings.api.ts` pentru că ecranul e în aceeași zonă, dar **nu e un
 * nomenclator**: pachetul are preț și monedă, deci n-are cum să treacă prin
 * `/catalog/:kind`. Endpoint-ul e `/packages`, cale proprie.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/settings`: `apps/crm` e
 * `scope:fe` și n-are voie să depindă de backend.
 */

const packageSchema = z.object({
  id: z.uuid(),
  typeId: z.uuid(),
  typeName: z.string(),
  /**
   * Iconul tipului, trimis deja normalizat de server. `z.string()`, nu `z.enum(...)`, din
   * același motiv ca la nomenclatoare: un nume necunoscut trebuie să coste un simbol, nu
   * catalogul întreg picat la `parse`.
   */
  typeIcon: z.string(),
  name: z.string(),
  /**
   * **String, nu `z.number()`.** Coloana e `DECIMAL(12,2)`; citită ca număr, `49.99` ar
   * deveni `49.990000000000002`, iar trimisă înapoi s-ar rotunji. Suma circulă ca text de
   * la bază până la `Intl.NumberFormat`, care e singurul loc care o convertește.
   */
  costDefault: z.string(),
  currency: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  /** Dacă `costDefault` e cu TVA inclus. Nu e cota — aia aparține facturării. */
  priceIncludesVat: z.boolean(),
  status: z.boolean(),
});

export type Package = z.infer<typeof packageSchema>;

export const PACKAGES_QUERY_KEY = ['packages'] as const;

/**
 * `includeInactive` face parte din cheia de cache, nu doar din URL.
 *
 * Ecranul de administrare cere și pachetele dezactivate; formularul de abonament, doar pe
 * cele active. Cu o cheie comună, cine deschide întâi administrarea ar vedea pachete
 * scoase din ofertă în lista de alegere.
 */
export const packagesQueryOptions = (includeInactive = false) =>
  queryOptions({
    queryKey: [...PACKAGES_QUERY_KEY, { includeInactive }] as const,
    queryFn: () =>
      apiGet(
        `/packages?includeInactive=${includeInactive ? 'true' : 'false'}`,
        z.object({ items: z.array(packageSchema) }),
      ).then((body) => body.items),
  });

export interface PackageInput {
  readonly typeId: string;
  /** Text, ca tot ce iese dintr-un `<input>`. Normalizarea sumei o face serverul. */
  readonly costDefault: string;
  readonly name: string;
  readonly currency: 1 | 2 | 3;
  readonly priceIncludesVat: boolean;
  readonly status: boolean;
}

export function createPackage(input: PackageInput): Promise<Package> {
  return apiPost('/packages', input, z.object({ item: packageSchema })).then((body) => body.item);
}

export function updatePackage(id: string, input: PackageInput): Promise<Package> {
  return apiPatch(`/packages/${id}`, input, z.object({ item: packageSchema })).then(
    (body) => body.item,
  );
}
